import React, { useEffect, useState, useCallback, useRef } from "react";
import axiosClient from "../../../api/axiosClient";
import "./GalleryPage.css";

/* ------------------------------------------------------------------ */
/*  Wired to the real GalleryAPIView:                                 */
/*    GET    /api/v1/gallery/        -> [{ id, tag, description,      */
/*                                          status, photo }]          */
/*    POST   /api/v1/gallery/        -> multipart { photo, tag,       */
/*                                          description }             */
/*    PUT    /api/v1/gallery/<id>/   -> multipart, same fields        */
/*    DELETE /api/v1/gallery/<id>/   -> remove one photo              */
/*  Response envelope: {status, error, message, data}                 */
/* ------------------------------------------------------------------ */
const GALLERY_ENDPOINT = "/api/v1/gallery/";

const EMPTY_FORM = { tag: "", description: "" };

export default function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [toast, setToast] = useState("");

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fileRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);

  // null => "add new" mode. an object => editing that existing photo.
  const [editingItem, setEditingItem] = useState(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const { data } = await axiosClient.get(GALLERY_ENDPOINT);
      setPhotos(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      setErrorMsg(
        err?.response?.status === 404
          ? "Gallery API isn't reachable — check the /api/v1/gallery/ route on the backend."
          : err?.response?.data?.message || "Couldn't load the gallery."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  function resetForm() {
    setForm(EMPTY_FORM);
    setPendingFile(null);
    setPendingPreview(null);
    setEditingItem(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function startEdit(item) {
    setEditingItem(item);
    setForm({ tag: item.tag || "", description: item.description || "" });
    setPendingFile(null);
    setPendingPreview(item.photo || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handlePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // a new photo is required when adding; when editing, keep the old one
    // if the admin didn't pick a replacement.
    if (!editingItem && !pendingFile) return;

    setSaving(true);
    setErrorMsg("");

    const body = new FormData();
    if (pendingFile) body.append("photo", pendingFile);
    body.append("tag", form.tag.trim());
    body.append("description", form.description.trim());

    try {
      if (editingItem) {
        const { data } = await axiosClient.put(
          `${GALLERY_ENDPOINT}${editingItem.id}/`,
          body,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setPhotos((prev) =>
          prev.map((p) => (p.id === editingItem.id ? { ...p, ...data?.data } : p))
        );
        setToast("Photo updated.");
      } else {
        const { data } = await axiosClient.post(GALLERY_ENDPOINT, body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.data) {
          setPhotos((prev) => [data.data, ...prev]);
        } else {
          await fetchPhotos();
        }
        setToast("Photo added.");
      }
      resetForm();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this photo? This can't be undone.")) return;
    setDeletingId(id);
    try {
      await axiosClient.delete(`${GALLERY_ENDPOINT}${id}/`);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      if (editingItem?.id === id) resetForm();
      setToast("Photo deleted.");
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Couldn't delete that photo.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="gpage">
      <div className="gpage__head">
        <div>
          <p className="gpage__eyebrow">Showroom</p>
          <h1 className="gpage__title">Gallery</h1>
        </div>
      </div>

      {toast && <div className="gpage__banner gpage__banner--ok">{toast}</div>}
      {errorMsg && <div className="gpage__banner">{errorMsg}</div>}

      <form className="gpage__uploader" onSubmit={handleSubmit}>
        <label className="gpage__drop" htmlFor="gpage-file">
          {pendingPreview ? (
            <img src={pendingPreview} alt="Selected upload preview" />
          ) : (
            <span>Click to choose a photo</span>
          )}
          <input id="gpage-file" ref={fileRef} type="file" accept="image/*" onChange={handlePick} hidden />
        </label>
        <div className="gpage__uploader-fields">
          <input
            className="gpage__caption"
            placeholder="Tag (e.g. Treadmill section, Tallakulam showroom)"
            value={form.tag}
            onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
          />
          <textarea
            className="gpage__caption"
            placeholder="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={2}
          />
          <div className="gpage__uploader-actions">
            <button
              className="gpage__upload-btn"
              type="submit"
              disabled={saving || (!editingItem && !pendingFile)}
            >
              {saving ? "Saving…" : editingItem ? "Save changes" : "Upload photo"}
            </button>
            {editingItem && (
              <button type="button" className="gpage__cancel-btn" onClick={resetForm} disabled={saving}>
                Cancel edit
              </button>
            )}
          </div>
        </div>
      </form>

      <div className="gpage__grid">
        {loading && <p className="gpage__state">Loading gallery…</p>}
        {!loading && photos.length === 0 && !errorMsg && (
          <p className="gpage__state">No photos yet — upload your first showroom shot above.</p>
        )}
        {!loading && photos.map((p) => (
          <div className={`gpage__card ${editingItem?.id === p.id ? "gpage__card--editing" : ""}`} key={p.id}>
            <img src={p.photo} alt={p.description || p.tag || "Gallery photo"} />
            {(p.tag || p.description) && (
              <span className="gpage__caption-tag">{p.tag || p.description}</span>
            )}
            <div className="gpage__card-actions">
              <button className="gpage__edit" onClick={() => startEdit(p)} aria-label="Edit photo">
                Edit
              </button>
              <button
                className="gpage__delete"
                onClick={() => handleDelete(p.id)}
                disabled={deletingId === p.id}
                aria-label="Delete photo"
              >
                {deletingId === p.id ? "…" : "×"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}